import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Wallet, MessagesSquare, HeartHandshake, Briefcase, Route, Bot, Trophy, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import Logo from "@/components/Logo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const SKILLBUDDY_URL = "https://www.skillbuddy.net";

const EASE = [0.22, 1, 0.36, 1];

const SKILLS = [
  { icon: Wallet, title: "Money & Finance", text: "Budgeting, saving, and smart money habits taught through real-life scenarios." },
  { icon: MessagesSquare, title: "Communication", text: "Conversations, interviews, and presentations — practiced with an AI that listens." },
  { icon: HeartHandshake, title: "Emotional Intelligence", text: "Self-awareness, empathy, and resilience built through guided reflection." },
  { icon: Briefcase, title: "Career Readiness", text: "Workplace skills, decision-making, and professionalism for the real world." },
];

const STEPS = [
  { icon: Route, title: "Personalized Path", text: "Skillbuddy assesses where you are and builds a life-skills path that fits your goals." },
  { icon: Bot, title: "Learn by Doing", text: "Chat through realistic scenarios and get instant, judgment-free feedback from your AI tutor." },
  { icon: Trophy, title: "Track Real Growth", text: "Watch skills level up with milestones that map to everyday life — not just test scores." },
];

export default function Skillbuddy() {
  return (
    <div className="min-h-screen bg-nexlight text-nexink font-sans" data-testid="skillbuddy-page">
      <Seo
        title="Skillbuddy — AI Tutor for Life Skills | NexOrbit Solutions"
        siteName="NexOrbit Solutions"
        description="Skillbuddy is an AI-supported learning companion focused on life skills — money, communication, emotional intelligence, and career readiness. Currently in pilot."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Skillbuddy",
          applicationCategory: "EducationalApplication",
          description: "AI tutor for life skills.",
          brand: { "@type": "Organization", name: "NexOrbit Solutions LLP" },
        }}
      />

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#001F5C]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <a href="/" aria-label="Back to NexOrbit Solutions home" data-testid="skillbuddy-logo-link">
            <Logo />
          </a>
          <a
            href={SKILLBUDDY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="skillbuddy-nav-cta"
            className="inline-flex items-center gap-2 rounded-full bg-nexorange px-5 py-2.5 text-sm font-semibold text-nexblue transition-transform duration-300 hover:scale-[1.04]"
          >
            Visit Skillbuddy
            <ArrowUpRight size={16} />
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-nexblue py-24 sm:py-32">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="orbit-ring w-[600px] h-[600px] -right-40 -top-40 animate-spin-slow" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,168,232,0.12),transparent_55%)]" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-10 bg-nexorange" />
              <span className="text-xs font-semibold tracking-[0.35em] text-nexcyan">AI EDTECH PRODUCTS</span>
            </motion.div>

            <h1 className="font-display font-bold tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }}>
                  <a
                    href={SKILLBUDDY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="skillbuddy-hero-link"
                    className="text-nexcyan underline decoration-nexorange/60 decoration-4 underline-offset-8 transition-colors hover:text-nexorange"
                  >
                    Skillbuddy
                  </a>
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: EASE }}>
                  Your AI Tutor for <span className="text-nexorange">Life Skills.</span>
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
              data-testid="skillbuddy-subheading"
            >
              School teaches subjects. Life also requires skills. Skillbuddy is an AI-supported learning companion that helps children develop practical life skills through conversation, practice and play.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href={SKILLBUDDY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="skillbuddy-try-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-nexorange px-7 py-3.5 text-sm font-bold text-nexblue transition-transform duration-300 hover:scale-[1.05] hover:shadow-[0_12px_32px_rgba(255,149,0,0.4)]"
              >
                Join the Pilot
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <span className="inline-flex items-center gap-2 rounded-full border border-nexorange/50 bg-nexorange/10 px-4 py-3 text-xs font-bold tracking-[0.2em] text-nexorange" data-testid="skillbuddy-pilot-badge">
                IN PILOT
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm text-white/70">
                <Sparkles size={15} className="text-nexcyan" />
                From NexOrbit Solutions LLP
              </span>
            </motion.div>
          </div>
        </section>

        <section className="py-24 sm:py-28" data-testid="skillbuddy-skills-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal className="max-w-3xl">
              <div className="flex items-center gap-4 mb-5">
                <span className="font-display text-sm font-bold text-nexorange">01</span>
                <span className="h-px w-12 bg-nexblue/25" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-nexcyan">What It Teaches</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-nexblue" data-testid="skillbuddy-skills-heading">
                Skills for Life, Not Just Exams
              </h2>
            </Reveal>
            <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SKILLS.map(({ icon: Icon, title, text }) => (
                <StaggerItem key={title}>
                  <div
                    data-testid={`skillbuddy-skill-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group h-full rounded-2xl border border-nexblue/10 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:border-nexcyan/50"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-nexblue text-nexcyan transition-colors duration-300 group-hover:bg-nexcyan group-hover:text-white">
                      <Icon size={22} />
                    </div>
                    <h3 className="mt-5 font-display text-lg font-semibold text-nexblue">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-nexink/70">{text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section className="py-24 sm:py-28 bg-white" data-testid="skillbuddy-how-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal className="max-w-3xl">
              <div className="flex items-center gap-4 mb-5">
                <span className="font-display text-sm font-bold text-nexorange">02</span>
                <span className="h-px w-12 bg-nexblue/25" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-nexcyan">How It Works</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-nexblue" data-testid="skillbuddy-how-heading">
                A Tutor That Meets You Where You Are
              </h2>
            </Reveal>
            <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
              {STEPS.map(({ icon: Icon, title, text }, i) => (
                <StaggerItem key={title}>
                  <div className="relative h-full rounded-2xl border border-nexblue/10 bg-nexlight p-8" data-testid={`skillbuddy-step-${i + 1}`}>
                    <span className="absolute right-6 top-4 font-display text-5xl font-bold text-nexblue/[0.06]" aria-hidden="true">0{i + 1}</span>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-nexorange/15 text-nexorange">
                      <Icon size={22} />
                    </div>
                    <h3 className="mt-5 font-display text-xl font-semibold text-nexblue">{title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-nexink/70">{text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section className="py-24 sm:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl bg-nexblue px-8 py-14 sm:px-14 text-center" data-testid="skillbuddy-cta-banner">
                <div className="orbit-ring w-[420px] h-[420px] -left-32 -bottom-32" style={{ borderColor: "rgba(0,168,232,0.15)" }} aria-hidden="true" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,149,0,0.10),transparent_55%)]" aria-hidden="true" />
                <h2 className="relative font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
                  Start Building Life Skills Today
                </h2>
                <p className="relative mt-4 text-base text-white/65 max-w-xl mx-auto">
                  <a href={SKILLBUDDY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-nexcyan hover:text-nexorange transition-colors" data-testid="skillbuddy-banner-link">
                    Skillbuddy
                  </a>{" "}
                  is currently in pilot — join early, help shape what it becomes, and be first in when it opens up.
                </p>
                <a
                  href={SKILLBUDDY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="skillbuddy-banner-cta"
                  className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-nexorange px-8 py-4 text-sm font-bold text-nexblue transition-transform duration-300 hover:scale-[1.05] hover:shadow-[0_12px_32px_rgba(255,149,0,0.4)]"
                >
                  Join the Pilot at skillbuddy.net
                  <ArrowUpRight size={17} />
                </a>
              </div>
            </Reveal>
            <div className="mt-10 text-center">
              <a href="/" className="inline-flex items-center gap-2 text-sm font-semibold text-nexink/50 transition-colors hover:text-nexcyan" data-testid="skillbuddy-back-home">
                <ArrowLeft size={15} /> Back to NexOrbit Solutions
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
